// UI strings for the translator's word tools (phase 3 items 12–15, phase 4 items 13–15 and 19):
// why a reading, swapping a word or a sense, the back-translation, help for a missing word, what
// an alternative changes, learner mode and the pronunciation line. Kept apart from
// areas/translator.ts so the two can change independently. Finnish is written in full; en has
// exactly the same shape. Functions that take an HV word return parts (string | the word) so the
// screen can set it in the HV face.
import type { LearnState, NoteConstruction } from '../../translator/constructions';
import type { WhyKey } from '../../translator/why';
import { defineArea } from '../area';

type Parts<W> = (string | W)[];

export const translatorToolsFi = {
  // ---- 12: why this reading ----
  whyHead: 'Miksi tämä tulkinta',
  why: {
    forced: () => 'Valitsit tämän tulkinnan itse.',
    only: () => 'Sanalla on vain tämä tulkinta.',
    context: () => 'Lauseyhteys ratkaisi: tämä tulkinta sopii lauseen muihin sanoihin, kuten yllä kerrotaan.',
    nearTie: () => 'Muut tulkinnat ovat lähes yhtä todennäköisiä. Katso ne alta ja vaihda, jos lause tarkoittaa muuta.',
    likelier: () => 'Tämä on tavallisempi tulkinta, eikä mikään lauseessa puhu sitä vastaan.',
    endingOnly: () => 'Sana tunnistettiin vain päätteestä, joten tulkinta on epävarma.',
    folded: () => 'Sana luettiin ilman pituusmerkkejä (ā ē ī ō ū ȳ). Tarkista, että tarkoitit tätä.',
    generated: (w?: string) =>
      w
        ? `Kääntäjä valitsi tämän sanan kirjoittamallesi sanalle ”${w}” ja taivutti sen lauseen mukaan.`
        : 'Kääntäjä valitsi tämän sanan ja taivutti sen lauseen mukaan.',
    swapped: (w?: string) =>
      w
        ? `Vaihdoit tämän sanan itse kirjoittamallesi sanalle ”${w}”. Lause taivutettiin uudelleen sen mukaan.`
        : 'Vaihdoit tämän sanan itse. Lause taivutettiin uudelleen sen mukaan.',
  } as Record<WhyKey, (w?: string) => string>,
  sourceWord: 'Kirjoittamasi sana',

  // ---- 13: swap a word / a sense ----
  swapHead: 'Vaihda sana',
  swapIntro: 'Valitse toinen sana. Lause käännetään uudelleen, ja sanat taipuvat sen mukaan.',
  swapSourceWord: (w: string) => `merkitys ”${w}”`,
  swapSynonym: (w: string) => `myös ”${w}”`,
  swapUse: 'Käytä',
  swapUseAria: (hv: string, gloss: string) => `Käytä sanaa ${hv}${gloss ? ` (${gloss})` : ''}`,
  swapNone: 'Sanakirjasta ei löytynyt muita sanoja tälle merkitykselle.',
  swapReset: 'Palauta kääntäjän valinta',
  yourChoice: 'sinun valintasi',
  ours: 'meidän',
  senseHead: 'Merkitys käännöksessä',
  senseIntro: 'Valitse, millä merkityksellä sana käännetään. Lause käännetään uudelleen.',
  senseUseAria: (sense: string) => `Käännä merkityksellä ”${sense}”`,
  senseReset: 'Anna kääntäjän valita',

  // ---- 14: back-translation ----
  backHead: { fi: 'Takaisin suomeksi', en: 'Takaisin englanniksi' } as Record<'fi' | 'en', string>,
  backSame: 'Merkitys säilyi: takaisinkäännös on sama kuin kirjoittamasi lause.',
  backHint: 'Takaisinkäännös näyttää, miten kääntäjä itse lukee valyriankielisen tuloksen. Ero ei aina ole virhe, mutta tarkista se.',
  backOtherWord: <W>(w: W, head: W): Parts<W> => [w, ' luetaan tässä sanaksi ', head, '.'],
  backOtherForm: <W>(w: W, what: string): Parts<W> => [w, ` luetaan tässä toisin: ${what}.`],
  backUnread: <W>(w: W): Parts<W> => ['Sanaa ', w, ' ei tunnistettu takaisin luettaessa.'],
  backMissing: (words: string) => `Takaisinkäännöksestä puuttuu: ${words}.`,
  backCapped: 'Kaksi merkkiä viittaa samaan suuntaan, joten varmuudeksi näytetään enintään ”melko varma”.',
  backReason: 'takaisinkäännös poikkeaa',

  // ---- 15: missing word ----
  missingHead: 'Mitä tilalle?',
  nearHead: 'Lähimmät valyrian sanat',
  nearSpelling: 'samannäköinen sana',
  nearNone: 'Sanakirjasta ei löytynyt lähellä olevia sanoja.',
  nearUse: (w: string) => `Kirjoita tilalle ”${w}”`,
  paraHead: 'Kiertoilmaus tutuista sanoista',
  paraNote: 'Kääntäjän kokoama kiertoilmaus: valyriankieliset sanat ovat sanakirjasta, mutta ilmaus ei ole Petersonin. Tarkista se ennen kuin käytät sitä.',
  paraUse: 'Käytä kiertoilmausta',
  needIntro:
    'Pyyntö menee Omat sanat -listalle. Kumpi tahansa voi ehdottaa sille valyriankielistä sanaa, ja toinen hyväksyy sen. Hyväksytty sana merkitään teidän omaksi sanaksenne (”meidän”).',
  needOneTap: 'Pyydä sanaa omiin sanoihin',
  needSent: 'Pyyntö on Omat sanat -listalla.',
  needFailed: 'Pyyntöä ei saatu tallennettua. Yritä uudelleen.',
  fullForm: 'Ehdota sanaa lomakkeella',

  // ---- P4-13: what an alternative changes ----
  altWord: <W>(from: W, to: W): Parts<W> => ['Toinen sana: ', from, ' → ', to],
  altForm: <W>(w: W, what: string): Parts<W> => ['Sama sana eri muodossa: ', w, ` – ${what}`],
  altOrder: 'Sama lause eri sanajärjestyksessä.',
  altWording: (from: string, to: string) => (from && to ? `Eri sanamuoto: ”${from}” → ”${to}”` : from ? `Ilman sanoja ”${from}”` : `Lisäksi ”${to}”`),
  altUse: 'Käytä tätä',
  altUseAria: (alt: string) => `Käytä vaihtoehtoa: ${alt}`,

  // ---- P4-14: learner mode ----
  learner: 'Oppimistila',
  learnerHead: 'Rakenteet tässä lauseessa',
  learnerIntro: 'Kukin rakenne linkittyy oppituntiin, joka opettaa sen, ja Kielioppi-sivun kohtaan. Rakenteet, joita ette ole vielä opiskelleet, on merkitty.',
  learnerNone: 'Lauseessa on vain perusmuotoja: nominatiivi, yksikkö ja preesens.',
  lesson: (n: number, title: string) => `Oppitunti ${n}: ${title}`,
  grammar: 'Kielioppi',
  state: { passed: 'opittu', open: 'avoinna', later: 'ei vielä opittu' } as Record<LearnState, string>,
  laterNote: (n: number) => `Tulee vastaan oppitunnilla ${n}.`,
  inWords: 'Sanoissa',
  tokenLessons: 'Opit tämän',
  note: {
    relative: 'relatiivilause',
    linker: 'sivulause ja sen sidesana',
    negation: 'kielto',
    negCommand: 'kielteinen käsky',
    obligation: 'velvollisuus (bēvilagon)',
    lengthening: 'loppuvokaalin pidennys ”ja”-sanan sijaan',
    passive: 'passiivi',
    address: 'puhuttelu (vokatiivi)',
    numerals: 'lukusanat',
    valence: 'verbin vaatima sija',
    wish: 'toive ja lupa',
    locativeBe: '”olla jossakin” (ilagon)',
    droppedPronoun: 'persoonapronomini jää pois',
    wordOrder: 'sanajärjestys: verbi lopussa',
  } as Record<NoteConstruction, string>,

  // ---- P4-15: pronunciation ----
  sayHead: 'Ääntäminen',
  sayHint: 'Painollinen tavu isoin kirjaimin, IPA vinoviivojen välissä. Paino on laskettu valyrian painosäännöistä, ei äänitteestä.',
  sayAria: (words: string) => `Ääntäminen: ${words}`,
};

export type TranslatorToolsMessages = typeof translatorToolsFi;

export const translatorToolsEn: TranslatorToolsMessages = {
  whyHead: 'Why this reading',
  why: {
    forced: () => 'You chose this reading yourself.',
    only: () => 'This word has only this reading.',
    context: () => 'The sentence decided: this reading fits the other words, as explained above.',
    nearTie: () => 'The other readings are almost as likely. See them below and switch if the sentence means something else.',
    likelier: () => 'This is the more common reading, and nothing in the sentence speaks against it.',
    endingOnly: () => 'The word was recognised by its ending only, so the reading is uncertain.',
    folded: () => 'The word was read without its long-vowel marks (ā ē ī ō ū ȳ). Check that you meant this.',
    generated: (w?: string) =>
      w
        ? `The translator chose this word for your word “${w}” and inflected it for the sentence.`
        : 'The translator chose this word and inflected it for the sentence.',
    swapped: (w?: string) =>
      w
        ? `You swapped in this word for your word “${w}”. The sentence was inflected again to fit it.`
        : 'You swapped in this word yourself. The sentence was inflected again to fit it.',
  } as Record<WhyKey, (w?: string) => string>,
  sourceWord: 'Your word',

  swapHead: 'Swap the word',
  swapIntro: 'Pick another word. The sentence is translated again and the words are inflected to fit it.',
  swapSourceWord: (w: string) => `meaning “${w}”`,
  swapSynonym: (w: string) => `also “${w}”`,
  swapUse: 'Use',
  swapUseAria: (hv: string, gloss: string) => `Use the word ${hv}${gloss ? ` (${gloss})` : ''}`,
  swapNone: 'The dictionary has no other words for this meaning.',
  swapReset: 'Back to the translator’s choice',
  yourChoice: 'your choice',
  ours: 'ours',
  senseHead: 'Meaning in the translation',
  senseIntro: 'Pick the meaning the word is translated with. The sentence is translated again.',
  senseUseAria: (sense: string) => `Translate with the meaning “${sense}”`,
  senseReset: 'Let the translator choose',

  backHead: { fi: 'Back into Finnish', en: 'Back into English' } as Record<'fi' | 'en', string>,
  backSame: 'The meaning survived: the back-translation is the sentence you wrote.',
  backHint: 'The back-translation shows how the translator itself reads the High Valyrian result. A difference is not always a mistake, but check it.',
  backOtherWord: <W>(w: W, head: W): Parts<W> => [w, ' reads here as the word ', head, '.'],
  backOtherForm: <W>(w: W, what: string): Parts<W> => [w, ` reads differently here: ${what}.`],
  backUnread: <W>(w: W): Parts<W> => ['The word ', w, ' was not recognised when read back.'],
  backMissing: (words: string) => `Missing from the back-translation: ${words}.`,
  backCapped: 'Two signs point the same way, so the confidence shown is at most “fairly sure”.',
  backReason: 'the back-translation differs',

  missingHead: 'What instead?',
  nearHead: 'Nearest High Valyrian words',
  nearSpelling: 'similar-looking word',
  nearNone: 'The dictionary has no words close to this one.',
  nearUse: (w: string) => `Write “${w}” instead`,
  paraHead: 'A paraphrase from known words',
  paraNote:
    'A paraphrase put together by the translator: the High Valyrian words are from the dictionary, but the expression is not Peterson’s. Check it before you use it.',
  paraUse: 'Use the paraphrase',
  needIntro:
    'The request goes to the Own words list. Either of you can propose a High Valyrian word for it, and the other approves it. An approved word is marked as your own word (“ours”).',
  needOneTap: 'Request a word for our own words',
  needSent: 'The request is on the Own words list.',
  needFailed: 'The request could not be saved. Try again.',
  fullForm: 'Propose a word with the form',

  altWord: <W>(from: W, to: W): Parts<W> => ['Another word: ', from, ' → ', to],
  altForm: <W>(w: W, what: string): Parts<W> => ['The same word in another form: ', w, ` – ${what}`],
  altOrder: 'The same sentence in another word order.',
  altWording: (from: string, to: string) => (from && to ? `Different wording: “${from}” → “${to}”` : from ? `Without “${from}”` : `Adds “${to}”`),
  altUse: 'Use this',
  altUseAria: (alt: string) => `Use the alternative: ${alt}`,

  learner: 'Learner mode',
  learnerHead: 'Constructions in this sentence',
  learnerIntro:
    'Each construction links to the lesson that teaches it and to its place on the Grammar page. Constructions you have not studied yet are marked.',
  learnerNone: 'The sentence has only basic forms: nominative, singular and present.',
  lesson: (n: number, title: string) => `Lesson ${n}: ${title}`,
  grammar: 'Grammar',
  state: { passed: 'learned', open: 'open', later: 'not learned yet' } as Record<LearnState, string>,
  laterNote: (n: number) => `Comes up in lesson ${n}.`,
  inWords: 'In the words',
  tokenLessons: 'Where you learn this',
  note: {
    relative: 'relative clause',
    linker: 'subordinate clause and its linker',
    negation: 'negation',
    negCommand: 'negative command',
    obligation: 'obligation (bēvilagon)',
    lengthening: 'lengthened final vowel instead of “and”',
    passive: 'passive',
    address: 'address (vocative)',
    numerals: 'numerals',
    valence: 'case the verb requires',
    wish: 'wish and permission',
    locativeBe: '“be somewhere” (ilagon)',
    droppedPronoun: 'the personal pronoun is left out',
    wordOrder: 'word order: verb last',
  } as Record<NoteConstruction, string>,

  sayHead: 'Pronunciation',
  sayHint: 'The stressed syllable in capitals, IPA between slashes. Stress is worked out from the High Valyrian stress rules, not from a recording.',
  sayAria: (words: string) => `Pronunciation: ${words}`,
};

// Messages.trTools (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('trTools', translatorToolsFi, translatorToolsEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof translatorToolsFi> {}
}
